package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UpdateAcademyRequest
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Promise
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement

/**
 * 학원 설정(가이드 골격 수준, P0 최소). ADMIN 전용 — 사이드바가 TEACHER(EDITOR)에게 항목 자체를
 * 숨기지만, URL 직접 접근에 대한 방어는 여기서도 한 번 더 한다(가이드 §3.3 "권한 밖 URL 직접 접근").
 */
object SettingsPage {
    private val scope = MainScope()

    /** 학원 정보 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val ACADEMY_RETRY_ID = "academy-settings-retry"
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        if (session.activeMembership?.academyId != academyId || session.activePermission != Permission.ADMIN) {
            Router.replace("/app")
            return
        }
        scope.launch {
            // 단건이라 부분 실패는 없다. 문서 없음(빈 성공)과 못 읽음은 둘 다 폼을 못 그리지만,
            // 재시도가 의미 있는 쪽은 후자뿐이라 화면이 구분해 안내한다(#30).
            val result = repository.academiesResult(listOf(academyId))
            render(session, academyId, result.item, result.failure)
        }
    }

    private fun render(
        session: SessionState,
        academyId: String,
        academy: AcademyData?,
        failure: CommandFailure?,
    ) {
        val academySettings = academy?.let { academySettingsHtml(it) } ?: if (failure != null) {
            """<section class="eb-card">${readErrorHtml(failure, ACADEMY_RETRY_ID)}</section>"""
        } else {
            // 문서 자체가 없는 경우 — 재시도해도 달라지지 않으므로 다른 안내를 준다.
            """
            <section class="eb-card">
                <div class="eb-empty-state">
                    ${icon("icon-settings", "xl")}
                    <h2>학원 정보를 찾을 수 없어요</h2>
                    <p>학원이 삭제됐거나 접근 권한이 바뀌었을 수 있어요.</p>
                </div>
            </section>
            """.trimIndent()
        }
        val html = """
            <h1 class="eb-page-title">설정</h1>
            $academySettings
            <section class="eb-card" aria-label="학생 참여 코드">
                <h2>학생 참여 코드</h2>
                <p>안내문이나 게시판에 붙여 두면 학생이 이 코드로 가입을 신청할 수 있어요. 신청은 사이드바의 <strong>가입 신청</strong>에서 승인해요.</p>
                <p id="join-code-panel" aria-live="polite">${icon("icon-refresh", "md", "eb-spin")} 참여 코드를 불러오는 중이에요…</p>
                <p class="eb-form-error" id="join-code-error" role="alert" hidden></p>
                <div class="eb-notice-actions">
                    <button class="eb-button eb-button--ghost eb-button--sm" id="join-code-copy" type="button" disabled>코드 복사</button>
                    <button class="eb-button eb-button--ghost eb-button--sm" id="join-code-rotate" type="button" disabled>코드 바꾸기</button>
                </div>
            </section>
            <section class="eb-card">
                <h2>선생님 초대</h2>
                <p>선생님으로 참여할 분에게 전달할 초대 코드를 발급해요.</p>
                <button class="eb-button eb-button--primary" id="issue-teacher-invitation" type="button">선생님 초대 코드 발급</button>
            </section>
        """.trimIndent()
        DashboardShell.render(session, academyId, "settings", html) {
            (document.getElementById("issue-teacher-invitation") as? HTMLButtonElement)?.onclick = {
                InvitationIssueModal.openForTeacher(session, academyId)
                null
            }
            if (academy != null) wireAcademyForm(session, academyId, academy)
            wireRetry(ACADEMY_RETRY_ID) { open(session, academyId) }
            wireJoinCode(academyId)
        }
    }

    // ---- W7 학생 참여 코드 ----

    /**
     * 발급 콜러블이 멱등이라 표시용으로 그냥 부른다 — ACTIVE 코드가 있으면 그대로 오고,
     * 없을 때만 새로 만들어진다. 게시판에 이미 붙여 둔 코드를 무효화하는 것은 "코드 바꾸기"뿐이다.
     */
    private fun wireJoinCode(academyId: String) {
        scope.launch {
            val result = commandRepository.issueAcademyJoinCode(academyId)
            val code = result.code
            if (!result.isSuccess || code == null) {
                showJoinCodeState("참여 코드를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.")
                return@launch
            }
            showJoinCode(academyId, code)
        }
    }

    private fun showJoinCode(academyId: String, code: String) {
        // 서버가 입력에서 공백·하이픈을 제거하므로 표시는 읽기 좋게 4자씩 끊는다.
        val display = code.chunked(4).joinToString("-")
        (document.getElementById("join-code-panel") as? HTMLElement)?.innerHTML =
            """<strong>${display.escapeHtml()}</strong>"""
        (document.getElementById("join-code-copy") as? HTMLButtonElement)?.let { button ->
            button.disabled = false
            button.onclick = { copyJoinCode(display); null }
        }
        (document.getElementById("join-code-rotate") as? HTMLButtonElement)?.let { button ->
            button.disabled = false
            button.textContent = "코드 바꾸기"
            button.onclick = { armJoinCodeRotation(academyId, code); null }
        }
    }

    /** 회전은 되돌릴 수 없어 두 번 누르게 한다 — 첫 클릭이 버튼을 확인 문구로 바꾼다. */
    private fun armJoinCodeRotation(academyId: String, code: String) {
        val button = document.getElementById("join-code-rotate") as? HTMLButtonElement ?: return
        button.textContent = "기존 코드가 즉시 무효화돼요 — 다시 눌러 확정"
        button.onclick = {
            button.disabled = true
            (document.getElementById("join-code-copy") as? HTMLButtonElement)?.disabled = true
            scope.launch {
                val result = commandRepository.rotateAcademyJoinCode(academyId)
                val newCode = result.code
                if (result.isSuccess && newCode != null) {
                    Toast.success("참여 코드를 바꿨어요. 안내문의 코드도 새로 붙여 주세요.")
                    showJoinCode(academyId, newCode)
                } else {
                    (document.getElementById("join-code-error") as? HTMLElement)?.let { error ->
                        error.textContent = result.errorMessage ?: "참여 코드를 바꾸지 못했어요. 다시 시도해 주세요."
                        error.removeAttribute("hidden")
                    }
                    showJoinCode(academyId, code)
                }
            }
            null
        }
        // 5초 안에 확정하지 않으면 원래 상태로 되돌린다 — 실수로 무장된 채 남지 않게.
        window.setTimeout({
            if (button.textContent != "코드 바꾸기" && !button.disabled) {
                button.textContent = "코드 바꾸기"
                button.onclick = { armJoinCodeRotation(academyId, code); null }
            }
        }, 5000)
    }

    private fun copyJoinCode(display: String) {
        try {
            val clipboard = window.navigator.asDynamic().clipboard
            if (clipboard == null || jsTypeOf(clipboard.writeText) != "function") {
                showJoinCodeCopyError()
                return
            }
            val promise = clipboard.writeText(display) as? Promise<dynamic>
            if (promise == null) {
                showJoinCodeCopyError()
                return
            }
            promise.then(
                { _: dynamic -> Toast.success("참여 코드를 복사했어요."); Unit },
                { _: dynamic -> showJoinCodeCopyError(); Unit },
            )
        } catch (_: Throwable) {
            showJoinCodeCopyError()
        }
    }

    private fun showJoinCodeCopyError() {
        showJoinCodeState("복사하지 못했어요. 화면의 코드를 직접 옮겨 적어 주세요.", isError = true)
    }

    private fun showJoinCodeState(message: String, isError: Boolean = false) {
        if (isError) {
            (document.getElementById("join-code-error") as? HTMLElement)?.let { error ->
                error.textContent = message
                error.removeAttribute("hidden")
            }
        } else {
            (document.getElementById("join-code-panel") as? HTMLElement)?.textContent = message
        }
    }

    private fun academySettingsHtml(academy: AcademyData): String = """
        <section class="eb-card">
            <h2>학원 정보</h2>
            <form id="academy-settings-form">
                <div class="eb-field"><label for="academy-settings-name">학원명</label><input id="academy-settings-name" maxlength="60" value="${academy.name.escapeHtml()}" required></div>
                <div class="eb-field"><label for="academy-settings-phone">대표 연락처 <span class="eb-meta">(선택)</span></label><input id="academy-settings-phone" type="tel" maxlength="20" value="${academy.phone.escapeHtml()}" placeholder="02-000-0000"></div>
                <div class="eb-field"><label for="academy-settings-address">주소 <span class="eb-meta">(선택)</span></label><input id="academy-settings-address" maxlength="200" value="${academy.address.escapeHtml()}"></div>
                <div class="eb-field"><label for="academy-settings-description">소개 <span class="eb-meta">(선택)</span></label><textarea class="eb-textarea" id="academy-settings-description" maxlength="500">${academy.description.escapeHtml()}</textarea></div>
                <p class="eb-form-error" id="academy-settings-error" role="alert" hidden></p>
                <button class="eb-button eb-button--primary" id="academy-settings-save" type="submit">저장</button>
            </form>
        </section>
    """.trimIndent()

    private fun wireAcademyForm(session: SessionState, academyId: String, academy: AcademyData) {
        val form = document.getElementById("academy-settings-form") as? HTMLFormElement ?: return
        form.onsubmit = { event ->
            event.preventDefault()
            submitAcademySettings(session, academyId, academy)
            null
        }
    }

    private fun submitAcademySettings(session: SessionState, academyId: String, academy: AcademyData) {
        val name = (document.getElementById("academy-settings-name") as? HTMLInputElement)?.value?.trim().orEmpty()
        val phone = (document.getElementById("academy-settings-phone") as? HTMLInputElement)?.value?.trim().orEmpty()
        val address = (document.getElementById("academy-settings-address") as? HTMLInputElement)?.value?.trim().orEmpty()
        val description = (document.getElementById("academy-settings-description") as? HTMLTextAreaElement)?.value?.trim().orEmpty()
        val error = document.getElementById("academy-settings-error") as? HTMLElement ?: return
        val save = document.getElementById("academy-settings-save") as? HTMLButtonElement ?: return

        fun showError(message: String) {
            error.textContent = message
            error.removeAttribute("hidden")
        }

        when {
            name.length !in 2..60 -> showError("학원명은 2~60자로 입력해 주세요.")
            phone.isNotEmpty() && !PHONE_PATTERN.matches(phone) -> showError("대표 연락처 형식을 확인해 주세요.")
            address.length > 200 -> showError("주소는 200자까지 입력할 수 있어요.")
            description.length > 500 -> showError("소개는 500자까지 입력할 수 있어요.")
            name == academy.name && phone == academy.phone && address == academy.address && description == academy.description ->
                showError("변경된 내용이 없어요.")
            else -> {
                error.setAttribute("hidden", "")
                save.disabled = true
                scope.launch {
                    try {
                        val result = commandRepository.updateAcademy(
                            UpdateAcademyRequest(academyId, name, address, phone, description),
                        )
                        if (result.isSuccess) {
                            Toast.success("학원 정보를 저장했어요.")
                            open(session, academyId)
                        } else {
                            showError(result.errorMessage ?: "저장에 실패했어요. 다시 시도해 주세요.")
                        }
                    } finally {
                        save.disabled = false
                    }
                }
            }
        }
    }

    private val PHONE_PATTERN = Regex("^[0-9+\\-() ]{7,20}$")
}
